
import React from 'react';
import * as SimpleIcons from 'simple-icons';
import { SocialPlatform } from '@/types/socialPlatform';

// Platform configuration with theme colors
const platformConfig: Record<string, { color: string; name: string; }> = {
  facebook: { color: '#1877F2', name: 'Facebook' },
  instagram: { color: '#E4405F', name: 'Instagram' },
  x: { color: '#000000', name: 'X' },
  tiktok: { color: '#000000', name: 'TikTok' },
  youtube: { color: '#FF0000', name: 'YouTube' },
};

interface CountrySocialLinksProps {
  countryName: string;
  socialPlatforms: SocialPlatform[];
}

const CountrySocialLinks: React.FC<CountrySocialLinksProps> = ({ 
  countryName, 
  socialPlatforms 
}) => {
  // Filter only active platforms
  const activePlatforms = socialPlatforms.filter(platform => platform.active);

  // If no active platforms, don't render anything
  if (activePlatforms.length === 0) return null;

  // Helper function to get icon from Simple Icons
  const getIcon = (platform: string) => {
    // Convert platform name to match simple-icons naming convention
    const iconName = platform === 'x' ? 'x' : platform.toLowerCase();
    
    try {
      // Try to find the icon in SimpleIcons
      const iconKey = `si${iconName.charAt(0).toUpperCase() + iconName.slice(1)}`;
      const icon = SimpleIcons[iconKey as keyof typeof SimpleIcons];
      
      if (icon && typeof icon === 'object' && 'path' in icon) {
        // Return the icon if it exists and has the expected shape
        return {
          path: icon.path,
          title: icon.title
        };
      }
    } catch (e) {
      console.error(`Icon for ${platform} not found`);
    }
    
    // Return null if icon not found
    return null;
  };

  return (
    <section className="py-12 bg-gray-50 dark:bg-gray-800/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-nation-blue dark:text-blue-300 mb-2">
            Connect with {countryName}
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            Follow our social media channels for updates and engagement
          </p>
        </div>
        
        <div className="flex flex-wrap justify-center gap-6 max-w-3xl mx-auto">
          {activePlatforms.map((platform) => {
            const icon = getIcon(platform.platform);
            const config = platformConfig[platform.platform] || { 
              color: '#666666', 
              name: platform.platform.charAt(0).toUpperCase() + platform.platform.slice(1)
            };
            
            return (
              <a 
                key={platform.platform}
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center transition-transform hover:scale-110 group"
                aria-label={`${config.name} for ${countryName}`}
              >
                <div 
                  className="w-16 h-16 flex items-center justify-center rounded-full mb-2 transition-shadow hover:shadow-lg" 
                  style={{ backgroundColor: config.color + '15' }}
                >
                  {icon && (
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: `<svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="${config.color}" d="${icon.path}"/></svg>`
                      }} 
                      className="w-8 h-8"
                    />
                  )}
                  {!icon && (
                    <span className="text-xl font-bold" style={{ color: config.color }}>
                      {config.name.charAt(0)}
                    </span>
                  )}
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-nation-blue dark:group-hover:text-blue-300">
                  {config.name}
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CountrySocialLinks;
